import type { BreedDatabaseConfig } from '../types/config';

/**
 * Returns the average weight for a breed as (min + max) / 2, rounded to integer.
 * Falls back to min-only or max-only if one is missing.
 * Returns null if the breed is not found or has no weight data.
 */
export function getAvgWeightKg(breedKey: string, db?: BreedDatabaseConfig): number | null {
  const entry = db?.breeds?.[breedKey];
  if (!entry) return null;
  const min = typeof entry.weight_min_kg === 'number' ? entry.weight_min_kg : null;
  const max = typeof entry.weight_max_kg === 'number' ? entry.weight_max_kg : null;
  if (min != null && max != null) return Math.round((min + max) / 2);
  if (min != null) return min;
  if (max != null) return max;
  return null;
}

/**
 * Approximate puppy weight at ~6 months as a percentage of adult average weight,
 * based on breed size. Larger breeds grow more slowly and reach a lower fraction
 * of adult weight by 6 months.
 *
 * Percentages based on standard canine growth curves:
 *   xs (<5 kg adult)   → 70%
 *   s  (5-10 kg)       → 65%
 *   m  (10-25 kg)      → 55%
 *   l  (25-45 kg)      → 50%
 *   xl (>45 kg)        → 45%
 */
const PUPPY_WEIGHT_FACTOR: Record<string, number> = {
  xs: 0.7,
  s: 0.65,
  m: 0.55,
  l: 0.5,
  xl: 0.45,
};

/**
 * Returns an estimated puppy weight at ~6 months for a breed, rounded to the
 * nearest integer. Falls back to getAvgWeightKg if breed_size is unknown.
 * Returns null if the breed is not found or has no weight data.
 */
export function getPuppyWeightKg(breedKey: string, db?: BreedDatabaseConfig): number | null {
  const avgWeight = getAvgWeightKg(breedKey, db);
  if (avgWeight === null) return null;
  const entry = db?.breeds?.[breedKey];
  const breedSize = entry?.breed_size?.toLowerCase() ?? '';
  const factor = PUPPY_WEIGHT_FACTOR[breedSize] ?? 0.55; // default to medium if unknown
  return Math.max(1, Math.round(avgWeight * factor));
}

/**
 * Returns the breed_insight string for a breed, or null if not found.
 */
export function getBreedInsight(breedKey: string, db?: BreedDatabaseConfig): string | null {
  return db?.breeds?.[breedKey]?.breed_insight ?? null;
}

/**
 * Returns compact breed weak-point bullets for P3. These are educational
 * prompts, not diagnoses.
 */
export function getBreedWeakPoints(breedKey: string, db?: BreedDatabaseConfig): string[] {
  const raw = db?.breeds?.[breedKey]?.weak_points;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean)
    .slice(0, 4);
}
